/* eslint-disable max-lines */
import React from "react";
import glamorous from "glamorous";

import Section from "../../components/Section";

const NarrowContainer = glamorous.div(({ theme }) => ({
  width: theme.main.maxWidths.text
}));

const WideContainer = glamorous.div(({ theme }) => ({
  maxWidth: theme.main.maxWidths.default,
  width: "100%"
}));

const VPATTable = glamorous.table(({ theme }) => ({
  marginBottom: "5rem",
  [theme.main.mediaQueries.mobile]: {
    fontSize: "75%"
  }
}));

const Accessibility = () => (
  <div>
    <Section css={{ paddingTop: "calc(100px + 1rem)" }}>
      <NarrowContainer>
        <h3>Accessibility</h3>
        <p>
          To help our clients and partners comply with accessibility standards, we publish information about how Manta is available to people with disabilities. Below you can find the Voluntary Product Accessibility Template, which outlines our compliance with accessibility requirements and design principles described in <a href="https://www.section508.gov" rel="noopener noreferrer" target="_blank">Section 508</a>, a government-wide IT accessibility program in the United States.
        </p>
        <p>Currently, the VPAT only refers to the <strong>patron role</strong>, and <strong>not</strong> the staff and admin roles.</p>
        <hr></hr>
        <p>If you have a disability and are having trouble accessing information on this website or need materials in an alternate format, please email <strong>support [at] manta.equipment</strong>.</p>
      </NarrowContainer>
    </Section>
    <Section css={{ paddingTop: 0 }}>
      <WideContainer>
        <h3>Voluntary Product Accessibility Template (VPAT)</h3>
        <VPATTable>
          <thead>
            <tr>
              <th className="table-heading" colSpan="3">
                Summary Table
              </th>
            </tr>
            <tr>
              <th>
                Criteria
              </th>
              <th>
                Supporting features
              </th>
              <th>
                Remarks
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Section 1194.21 Software Applications and Operating Systems
              </td>
              <td>
                Applicable
              </td>
              <td>
                Supports with exceptions
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.22 Web-based Internet Information and Applications
              </td>
              <td>
                Applicable
              </td>
              <td>
                Supports with exceptions
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.23 Telecommunications Products
              </td>
              <td>
                Not applicable
              </td>
              <td>
                -
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.24 Video and Multimedia Products
              </td>
              <td>
                Not applicable
              </td>
              <td>
                -
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.25 Self-Contained, Closed Products
              </td>
              <td>
                Not applicable
              </td>
              <td>
                -
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.26 Desktop and Portable Computers
              </td>
              <td>
                Not applicable
              </td>
              <td>
                -
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.31 Functional Performance Criteria
              </td>
              <td>
                Applicable
              </td>
              <td>
                Supports
              </td>
            </tr>
            <tr>
              <td>
                Section 1194.41 Information, Documentation, and Support
              </td>
              <td>
                Applicable
              </td>
              <td>
                Supports
              </td>
            </tr>
          </tbody>
        </VPATTable>

        <VPATTable>
          <thead>
            <tr>
              <th className="table-heading" colSpan="3">
                Section 1194.21 Software Applications and Operating
              </th>
            </tr>
            <tr>
              <glamorous.Th width="45%">
                Criteria
              </glamorous.Th>
              <glamorous.Th width="20%">
                Supporting features
              </glamorous.Th>
              <glamorous.Th width="35%">
                Remarks
              </glamorous.Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                (a) When software is designed to run on a system that has a keyboard, product
                functions shall be executable from a keyboard where the function itself or the
                result of performing a function can be discerned textually.
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                The Manta Equipment user interface is designed to be keyboard friendly, and most tasks can be executed only using a keyboard, with some exceptions:
                <ul>
                  <li>Adding a resource by navigating via the category menu</li>
                  <li>Manually navigating the timeline</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                (b) Applications shall not disrupt or disable activated features of other products
                that are identified as accessibility features, where those features are developed
                and documented according to industry standards. Applications also shall not disrupt
                or disable activated features of any operating system that are identified as
                accessibility features where the application programming interface for those
                accessibility features has been documented by the manufacturer of the operating
                system and is available to the product developer.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta Equipment does not interfere with or deactivate accessibility
                features of the user&apos;s operating system. Browser keyboard shortcuts, screen readers, magnifiers and other common accessibility tools are not affected.
              </td>
            </tr>
            <tr>
              <td>
                (c) A well-defined on-screen indication of the current focus shall be provided that
                moves among interactive interface elements as the input focus changes. The focus
                shall be programmatically exposed so that assistive technology can track focus and
                focus changes.
              </td>
              <td>
                Supports
              </td>
              <td>
                Focus of interface elements is programmatically exposed and signified visually through the use of outlines, changes in color or border. When relevant, outlines are color-coded to indicate usage.
              </td>
            </tr>
            <tr>
              <td>
                (d) Sufficient information about a user interface element including the identity,
                operation and state of the element shall be available to assistive technology. When
                an image represents a program element, the information conveyed by the image must
                also be available in text.
              </td>
              <td>
                Partially supports
              </td>
              <td>
                Role and state information is currently not provided for elements in the Manta user interface. Images are currently not used in the Manta interface, hence all relevant information is available textually.
              </td>
            </tr>
            <tr>
              <td>
                (e) When bitmap images are used to identify controls, status indicators, or other
                programmatic elements, the meaning assigned to those images shall be consistent
                throughout an application&apos;s performance.
              </td>
              <td>
                Supports
              </td>
              <td>
                Bitmap images are used consistently throughout Manta Equipment.
              </td>
            </tr>
            <tr>
              <td>
                (f) Textual information shall be provided through operating system functions for
                displaying text. The minimum information that shall be made available is text
                content, text input caret location, and text attributes.
              </td>
              <td>
                Supports
              </td>
              <td>
                Text is output using conventional elements, utilizing the operating system&apos;s functions for displaying text. 
              </td>
            </tr>
            <tr>
              <td>
                (g) Applications shall not override user selected contrast and color selections and
                other individual display attributes.
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                Contrast settings and color selections at the user level are not overridden by Manta. However, the ease of use of the user interface is reduced when using the highest contrast level on macOS. In particular:
                <ul>
                  <li>Some focusable element borders can no longer be seen</li>
                  <li>Focused elements cannot always be discerned</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                (h) When animation is displayed, the information shall be displayable in at least
                one non-animated presentation mode at the option of the user.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta Equipment does not make use of animation to convey information.
              </td>
            </tr>
            <tr>
              <td>
                (i) Color coding shall not be used as the only means of conveying information,
                indicating an action, prompting a response, or distinguishing a visual element.
              </td>
              <td>
                Does not support
              </td>
              <td>
                Many elements of the interface duplicate color-coded information as text or via icons, however, several key features heavily rely on color to distinguish properties of an item:
                <ul>
                  <li>The timeline feature of Manta currently only relies on color coding to distinguish between resources in different states, apart from late checkouts.</li>
                  <li>Unavailable items in the resources list are only marked by color tint.</li>
                  <li>Active application mode is only indicated by tinting the mode selection buttons.</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                (j) When a product permits a user to adjust color and contrast settings, a variety
                of color selections capable of producing a range of contrast levels shall be
                provided.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta Equipment does not have an option to modify the theme of the user interface.
              </td>
            </tr>
            <tr>
              <td>
                (k) Software shall not use flashing or blinking text, objects, or other elements
                having a flash or blink frequency greater than 2 Hz and lower than 55 Hz.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta Equipment does not use any flashing text or other content.
              </td>
            </tr>
            <tr>
              <td>
                (l) When electronic forms are used, the form shall allow people using assistive
                technology to access the information, field elements, and functionality required
                for completion and submission of the form, including all directions and cues.
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                The primary process of booking an item can be accomplished with the use of assistive technologies, although some limitations exist:
                <ul>
                  <li>Bookings notes currently cannot be added via keyboard only.</li>
                  <li>Booking start and end date must be entered manually, and cannot be picked from a calendar using arrow keys.</li>
                  <li>Several elements in the form do not have a role property defined.</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </VPATTable>

        <VPATTable>
          <thead>
            <tr>
              <th className="table-heading" colSpan="3">
                Section 1194.22 Web-based Internet Information and Applications
              </th>
            </tr>
            <tr>
              <glamorous.Th width="45%">
                Criteria
              </glamorous.Th>
              <glamorous.Th width="20%">
                Supporting features
              </glamorous.Th>
              <glamorous.Th width="35%">
                Remarks
              </glamorous.Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                (a) A text equivalent for every non-text element shall be provided (e.g., via
                &quot;alt&quot;, &quot;longdesc&quot;, or in element content).
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                Most, but not all graphic elements visible to users have text equivalents provided via the &quot;alt&quot; tag.
              </td>
            </tr>
            <tr>
              <td>
                (b) Equivalent alternatives for any multimedia presentation shall be synchronized
                with the presentation.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta Equipment does not use multimedia presentations.
              </td>
            </tr>
            <tr>
              <td>
                (c) Web pages shall be designed so that all information conveyed with color is also
                available without color, for example from context or markup.
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                All features conveyed with color on Manta are also conveyed via HTML markup and with the use of the &quote;role&quot; attribute, with a few minor exceptions:
                <ul>
                  <li>Unavailable resources in the resource panel</li>
                  <li>Resource status in the timeline</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                (d) Documents shall be organized so they are readable without requiring an
                associated style sheet.
              </td>
              <td>
                Does not support
              </td>
              <td>
                Manta is a single-page application and relies on style-sheets for functionality and ease of use, therefore it is not readable without the associated style sheets.
              </td>
            </tr>
            <tr>
              <td>
                (e) Redundant text links shall be provided for each active region of a server-side image map.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta does not use image maps.
              </td>
            </tr>
            <tr>
              <td>
                (f) Client-side image maps shall be provided instead of server-side image maps
                except where the regions cannot be defined with an available geometric shape.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta does not use image maps.
              </td>
            </tr>
            <tr>
              <td>
                (g) Row and column headers shall be identified for data tables.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta Equipment identifies headers for data tables.
              </td>
            </tr>
            <tr>
              <td>
                (h) Markup shall be used to associate data cells and header cells for data tables
                that have two or more logical levels of row or column headers.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                The patron role interface for Manta does not have data tables with two or more logical levels of row or column headers.
              </td>
            </tr>
            <tr>
              <td>
                (i) Frames shall be titled with text that facilitates frame identification and
                navigation.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta does not use frames.
              </td>
            </tr>
            <tr>
              <td>
                (j) Pages shall be designed to avoid causing the screen to flicker with a frequency
                greater than 2 Hz and lower than 55 Hz.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta Equipment does not cause screen flicker.
              </td>
            </tr>
            <tr>
              <td>
                (k) A text-only page, with equivalent information or functionality, shall be
                provided to make a web site comply with the provisions of this part, when
                compliance cannot be accomplished in any other way. The content of the text-only
                page shall be updated whenever the primary page changes.
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta Equipment is a single-page application, therefore it cannot be represented in a text-only format while ensuring functionality. Manta strives to ensure compliance with Section 1194.22 by complying with the other provisions listed.
              </td>
            </tr>
            <tr>
              <td>
                (l) When pages utilize scripting languages to display content, or to create
                interface elements, the information provided by the script shall be identified with
                functional text that can be read by assistive technology.
              </td>
              <td>
                Supports
              </td>
              <td>
                All elements created and content provided by scripting are either text-only or accompanied by text element that can be read using assistive technology.
              </td>
            </tr>
            <tr>
              <td>
                (m) When a web page requires that an applet, plug-in or other application be
                present on the client system to interpret page content, the page must provide a
                link to a plug-in or applet that complies with §1194.21(a) through (l).
              </td>
              <td>
                Not applicable
              </td>
              <td>
                Manta Equipment does not require applets, plug-ins or other third-party applications to be present on the user&apos;s system for operation.
              </td>
            </tr>
            <tr>
              <td>
                (n) When electronic forms are designed to be completed online, the form shall allow
                people using assistive technology to access the information, field elements, and
                functionality required for completion and submission of the form, including all
                directions and cues.
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                The primary process of booking an item can be accomplished with the use of assistive technologies, although some limitations exist:
                <ul>
                  <li>Bookings notes currently cannot be added via keyboard only.</li>
                  <li>Booking start and end date must be entered manually, and cannot be picked from a calendar using arrow keys.</li>
                  <li>Several elements in the form do not have a role property defined.</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td>
                (o) A method shall be provided that permits users to skip repetitive navigation
                links.
              </td>
              <td>
                Does not support
              </td>
              <td>
                Manta does not currently have a way to skip the navigation section of the application.
              </td>
            </tr>
            <tr>
              <td>
                (p) When a timed response is required, the user shall be alerted and given
                sufficient time to indicate more time is required.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta Equipment does not require a timed response for any of its core functionality.
              </td>
            </tr>
          </tbody>
        </VPATTable>

        <VPATTable>
          <thead>
            <tr>
              <th className="table-heading" colSpan="3">
                Section 1194.31 Functional Performance Criteria
              </th>
            </tr>
            <tr>
              <glamorous.Th width="45%">
                Criteria
              </glamorous.Th>
              <glamorous.Th width="20%">
                Supporting features
              </glamorous.Th>
              <glamorous.Th width="35%">
                Remarks
              </glamorous.Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                (a) At least one mode of operation and information retrieval that does not require
                user vision shall be provided, or support for assistive technology used by people
                who are blind or visually impaired shall be provided.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta supports the usage of screen readers for its core functionality.
              </td>
            </tr>
            <tr>
              <td>
                (b) At least one mode of operation and information retrieval that does not require
                visual acuity greater than 20/70 shall be provided in audio and enlarged print
                output working together or independently, or support for assistive technology used
                by people who are visually impaired shall be provided.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta does not provide tools to scale the font-size on the site, but Manta supports the usage of screen magnifiers.
              </td>
            </tr>
            <tr>
              <td>
                (c) At least one mode of operation and information retrieval that does not require
                user hearing shall be provided, or support for assistive technology used by people
                who are deaf or hard of hearing shall be provided.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta does not use audio throughout the application.
              </td>
            </tr>
            <tr>
              <td>
                (d) Where audio information is important for the use of a product, at least one
                mode of operation and information retrieval shall be provided in an enhanced
                auditory fashion, or support for assistive hearing devices shall be provided.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta does not use audio throughout the application.
              </td>
            </tr>
            <tr>
              <td>
                (e) At least one mode of operation and information retrieval that does not require
                user speech shall be provided, or support for assistive technology used by people
                with disabilities shall be provided.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta does not require user speech.
              </td>
            </tr>
            <tr>
              <td>
                (f) At least one mode of operation and information retrieval that does not require
                fine motor control or simultaneous actions and that is operable with limited reach
                and strength shall be provided.
              </td>
              <td>
                Supports with exceptions
              </td>
              <td>
                The Manta Equipment user interface is designed to be keyboard friendly, and most tasks can be executed only using a keyboard, without the need for simultaneous actions, with the exception of manually navigating the timeline (requires hold-and-drag motion).
              </td>
            </tr>
          </tbody>
        </VPATTable>

        <VPATTable>
          <thead>
            <tr>
              <th className="table-heading" colSpan="3">
                Section 1194.41 Information, Documentation and Support
              </th>
            </tr>
            <tr>
              <glamorous.Th width="45%">
                Criteria
              </glamorous.Th>
              <glamorous.Th width="20%">
                Supporting features
              </glamorous.Th>
              <glamorous.Th width="35%">
                Remarks
              </glamorous.Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                (a) Product support documentation provided to end-users shall be made available in
                alternate formats upon request, at no additional charge.
              </td>
              <td>
                Supports
              </td>
              <td>
                Manta Equipment is happy to provide documentation for any of the application&apos;s features in a user-requested format. This can be requested by contacting the support team via email.
              </td>
            </tr>
            <tr>
              <td>
                (b) End-users shall have access to a description of the accessibility and compatibility features of products in alternate formats or alternate methods upon request, at no additional charge.
              </td>
              <td>
                Supports
              </td>
              <td>-</td>
            </tr>
            <tr>
              <td>
                (c) Support services for products shall accommodate the communication needs of
                end-users with disabilities.
              </td>
              <td>
                Supports
              </td>
              <td>
                Support for Manta is available via text-based chat or voice call using a platform convenient for the end-user upon request.
              </td>
            </tr>
          </tbody>
        </VPATTable>

        <p>Last Edited on 2019-04-18</p>
      </WideContainer>
    </Section>
  </div>
);

export default Accessibility;



// WEBPACK FOOTER //
// ./src/pages/faq/accessibility.js